/* One shape for a person, whichever record they live in.

   An applicant and the student they become are two documents in two
   collections with two sets of field names for the same facts:
   `first_name`/`last_name` against `name`, `date_of_birth` against `dob`,
   a nested `address` object against six flat columns. The server already
   performs this translation once, at conversion time
   (`_converted_student_application_defaults` in server.py), but only in one
   direction and only at one moment, so the two admin pages could never render
   from the same components.

   This module is that translation written down: `applicationToPerson` and
   `studentToPerson` read either record into a common view model, and the
   `personPatchTo*` functions map edits back to the record's own field names.

   Codes, not labels, are canonical. The application side stores lowercase
   codes (`study_permit`) and the student side stores display strings
   (`Study Permit`); a code survives a round trip through a select element and
   a display string does not, so the view model always holds the code and the
   student translation happens at the edges.

   Two of those translations lose information: an application distinguishes
   `online_live` from `online_self_paced` where a student records only
   "Online", and `outside_canada` from `other` where a student records only
   "Other". `reconcileLossy` below is what stops a write-through from
   flattening the applicant's more specific answer. */

// ── Enum translation ────────────────────────────────────

// Mirrors APPLICATION_STATUS_TO_STUDENT_STATUS in backend/server.py.
export const STATUS_CODE_TO_STUDENT = {
  citizen: 'Citizen',
  permanent_resident: 'Permanent Resident',
  study_permit: 'Study Permit',
  work_permit: 'Work Permit',
  visitor_visa: 'Visitor',
  outside_canada: 'Other',
  other: 'Other',
};

// Mirrors APPLICATION_CAMPUS_TO_STUDENT_CAMPUS. This one is bijective.
export const CAMPUS_CODE_TO_STUDENT = {
  vancouver_downtown: 'Vancouver (Downtown)',
  online: 'Online',
  undecided: 'Undecided',
};

// Mirrors _ATTENDANCE_TO_STUDENT_DELIVERY.
export const ATTENDANCE_CODE_TO_STUDENT = {
  in_person: 'In Person',
  online_live: 'Online',
  online_self_paced: 'Online',
  blended: 'Hybrid',
};

// Mirrors APPLICATION_PATHWAY_TO_STUDENT_PATHWAY. Bijective.
export const PATHWAY_CODE_TO_STUDENT = {
  yes: 'Yes',
  no: 'No',
  undecided: 'Not Decided Yet',
};

/* Builds the reverse of a forward map. Where two codes share one student
   value the first code listed wins, which is why the forward maps above list
   the more common code first: "Online" comes back as `online_live`, not as
   `online_self_paced`. Callers that hold the applicant's original code should
   go through `reconcileLossy` rather than using this directly. */
function invert(forward) {
  const reverse = {};
  Object.entries(forward).forEach(([code, label]) => {
    if (!(label in reverse)) reverse[label] = code;
  });
  return reverse;
}

export const STUDENT_TO_STATUS_CODE = invert(STATUS_CODE_TO_STUDENT);
export const STUDENT_TO_CAMPUS_CODE = invert(CAMPUS_CODE_TO_STUDENT);
export const STUDENT_TO_ATTENDANCE_CODE = invert(ATTENDANCE_CODE_TO_STUDENT);
export const STUDENT_TO_PATHWAY_CODE = invert(PATHWAY_CODE_TO_STUDENT);

/* Translate a student's display string back to a code without discarding a
   more specific code we already hold.

   The student record cannot tell `online_self_paced` from `online_live` — it
   says only "Online". So when the value we already have still maps forward to
   what the student says, the student is not disagreeing with us and the
   original code stands. Only a genuine change falls through to the reverse
   map. */
export function reconcileLossy(currentCode, studentValue, forward, reverse) {
  const incoming = String(studentValue || '').trim();
  if (!incoming) return currentCode || '';
  const current = String(currentCode || '').trim();
  if (current && forward[current] === incoming) return current;
  return reverse[incoming] || '';
}

// ── Names ───────────────────────────────────────────────

/* Split a single stored name for student records written before `first_name`
   and `last_name` existed. The last whitespace-separated token is the family
   name and everything before it is the given name, so "Mary Jane Watson"
   splits as "Mary Jane" + "Watson". Records carrying the explicit fields
   never reach this. */
export function splitName(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

export function joinName(firstName, lastName) {
  return [String(firstName || '').trim(), String(lastName || '').trim()]
    .filter(Boolean)
    .join(' ');
}

// ── Phone ───────────────────────────────────────────────

/* Students store one phone string, applications store the country code apart
   from the number. Splitting is only ever a display convenience, so a string
   that does not start with a "+<digits>" token is kept whole as the number
   rather than guessed at. */
export function splitPhone(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\+\d{1,4})\s*(.*)$/);
  if (!match) return { phoneCountryCode: '', phoneNumber: raw };
  return { phoneCountryCode: match[1], phoneNumber: match[2].trim() };
}

export function joinPhone(countryCode, number) {
  return [String(countryCode || '').trim(), String(number || '').trim()]
    .filter(Boolean)
    .join(' ');
}

/* The French intake answers, as the view model names them. Listed once so the
   merge below can move them as the single related block they are. */
const FRENCH_VIEW_FIELDS = [
  'frenchGoal', 'frenchTargetLevel', 'frenchCurrentLevel',
  'frenchTestProof', 'frenchPlacementPreference',
];

// ── Reading ─────────────────────────────────────────────

function text(value) {
  return value === null || value === undefined ? '' : String(value);
}

function list(value) {
  return Array.isArray(value) ? value : [];
}

/* The empty view model. Every reader starts here so a caller can rely on
   every key existing, whatever the source record was missing. */
export function emptyPerson() {
  return {
    kind: '',
    firstName: '',
    lastName: '',
    preferredName: '',
    email: '',
    phoneCountryCode: '',
    phoneNumber: '',
    dateOfBirth: '',
    gender: '',
    citizenshipCountry: '',
    statusInCanada: '',
    statusExpiryDate: '',
    sinLast3: '',
    hasSin: false,
    address: { unit: '', street: '', city: '', provinceState: '', country: '', postalCode: '' },
    addressLegacy: '',
    usingAgency: false,
    agentId: '',
    agentFirstName: '',
    agentLastName: '',
    agencyCompanyName: '',
    agentPhone: '',
    agentEmail: '',
    agencyNotes: '',
    // Where the person heard about us. Stored under the same two names on both
    // records, so there is nothing to translate — see lib/heardAbout.js for the
    // vocabulary. An agency-linked record always reads back as `agency`.
    heardAboutUs: '',
    heardAboutUsDetail: '',
    /* The French intake answers (see lib/frenchIntake.js). Same field names on
       both records, so nothing is translated here either. */
    frenchGoal: '',
    frenchTargetLevel: '',
    frenchCurrentLevel: '',
    frenchTestProof: '',
    frenchPlacementPreference: '',
    program: '',
    selectedAddOns: [],
    intendedStart: '',
    campus: '',
    attendanceMode: '',
    universityPathway: '',
    englishProficiencyType: '',
    englishProficiencyScore: '',
    applicationFeePaid: false,
  };
}

export function applicationToPerson(app) {
  const source = app || {};
  const address = source.address && typeof source.address === 'object' ? source.address : {};
  return {
    ...emptyPerson(),
    kind: 'application',
    firstName: text(source.first_name),
    lastName: text(source.last_name),
    preferredName: text(source.preferred_name),
    email: text(source.email),
    phoneCountryCode: text(source.phone_country_code),
    phoneNumber: text(source.phone_number),
    dateOfBirth: text(source.date_of_birth),
    gender: text(source.gender),
    citizenshipCountry: text(source.country_of_citizenship),
    statusInCanada: text(source.status_in_canada),
    statusExpiryDate: text(source.status_expiry_date),
    sinLast3: text(source.sinLast3),
    hasSin: Boolean(source.sinLast3 || source.sinCiphertext),
    address: {
      unit: text(address.unit),
      street: text(address.street),
      city: text(address.city),
      provinceState: text(address.province_state),
      country: text(address.country),
      postalCode: text(address.postal_code),
    },
    usingAgency: source.using_agency === true,
    agentId: text(source.agent_id),
    agentFirstName: text(source.agent_first_name),
    agentLastName: text(source.agent_last_name),
    agencyCompanyName: text(source.agency_company_name),
    agentPhone: text(source.agent_phone),
    agentEmail: text(source.agent_email),
    agencyNotes: text(source.agency_notes),
    heardAboutUs: text(source.heard_about_us),
    heardAboutUsDetail: text(source.heard_about_us_detail),
    frenchGoal: text(source.french_goal),
    frenchTargetLevel: text(source.french_target_level),
    frenchCurrentLevel: text(source.french_current_level),
    frenchTestProof: text(source.french_test_proof),
    frenchPlacementPreference: text(source.french_placement_preference),
    program: text(source.program),
    selectedAddOns: list(source.selected_add_ons).map(text),
    intendedStart: text(source.intended_semester),
    campus: text(source.campus),
    attendanceMode: text(source.attendance_mode),
    // `pathway_interest` superseded `university_pathway`; conversion already
    // prefers it, so reading follows the same precedence.
    universityPathway: text(source.pathway_interest || source.university_pathway),
    englishProficiencyType: text(source.english_proficiency_type),
    englishProficiencyScore: text(source.english_proficiency_score),
    applicationFeePaid: source.application_fee_paid === true,
  };
}

export function studentToPerson(student) {
  const source = student || {};
  // `first_name`/`last_name` are the newer fields; `name` is what every record
  // written before them carries, and stays the derived display value.
  const explicitFirst = text(source.first_name).trim();
  const explicitLast = text(source.last_name).trim();
  const split = splitName(source.name);
  const phone = splitPhone(source.phone);
  return {
    ...emptyPerson(),
    kind: 'student',
    firstName: explicitFirst || split.firstName,
    lastName: explicitLast || split.lastName,
    preferredName: text(source.preferred_name),
    email: text(source.email),
    phoneCountryCode: phone.phoneCountryCode,
    phoneNumber: phone.phoneNumber,
    dateOfBirth: text(source.dob),
    gender: text(source.gender),
    citizenshipCountry: text(source.citizenship_country),
    statusInCanada: STUDENT_TO_STATUS_CODE[text(source.status_in_canada).trim()] || '',
    statusExpiryDate: text(source.status_expiry_date),
    sinLast3: text(source.sinLast3),
    hasSin: Boolean(source.sinLast3 || source.sinCiphertext || source.sin),
    address: {
      unit: text(source.unit_number),
      street: text(source.street_address),
      city: text(source.city),
      provinceState: text(source.province),
      country: text(source.country),
      postalCode: text(source.postal_code),
    },
    // Students written before the address was broken into columns carry one
    // formatted string. The page shows it only when no column is filled.
    addressLegacy: text(source.address),
    usingAgency: Boolean(source.using_agency || source.agent_id),
    agencyDataPresent: Object.prototype.hasOwnProperty.call(source, 'using_agency'),
    agentFirstName: text(source.agent_first_name),
    agentLastName: text(source.agent_last_name),
    agencyCompanyName: text(source.agency_company_name),
    agentPhone: text(source.agent_phone),
    agentEmail: text(source.agent_email),
    agencyNotes: text(source.agency_notes),
    heardAboutUs: text(source.heard_about_us),
    heardAboutUsDetail: text(source.heard_about_us_detail),
    frenchGoal: text(source.french_goal),
    frenchTargetLevel: text(source.french_target_level),
    frenchCurrentLevel: text(source.french_current_level),
    frenchTestProof: text(source.french_test_proof),
    frenchPlacementPreference: text(source.french_placement_preference),

    agentId: text(source.agent_id),
    program: text(source.program_name),
    selectedAddOns: list(source.selected_add_ons).map(text),
    intendedStart: text(source.start_date),
    campus: STUDENT_TO_CAMPUS_CODE[text(source.campus).trim()] || '',
    attendanceMode: STUDENT_TO_ATTENDANCE_CODE[text(source.delivery_method).trim()] || '',
    universityPathway: STUDENT_TO_PATHWAY_CODE[text(source.pathway_option).trim()] || '',
    englishProficiencyType: text(source.english_proficiency_type),
    englishProficiencyScore: text(source.english_proficiency_score),
    applicationFeePaid: source.application_fee_paid === true,
  };
}

export function recordToPerson(record, kind) {
  return kind === 'student' ? studentToPerson(record) : applicationToPerson(record);
}

// ── Writing ─────────────────────────────────────────────

/* Map an edited view model back to an application's own field names.

   Takes the whole model rather than a sparse patch: both detail pages edit
   through a full form object and send the fields they own, so a partial patch
   would only move the "which keys are set" bookkeeping somewhere else. */
export function personPatchToApplication(person) {
  const view = { ...emptyPerson(), ...(person || {}) };
  return {
    first_name: view.firstName,
    last_name: view.lastName,
    preferred_name: view.preferredName,
    email: view.email,
    phone_country_code: view.phoneCountryCode,
    phone_number: view.phoneNumber,
    date_of_birth: view.dateOfBirth,
    gender: view.gender,
    country_of_citizenship: view.citizenshipCountry,
    status_in_canada: view.statusInCanada,
    status_expiry_date: view.statusExpiryDate,
    address: {
      unit: view.address.unit,
      street: view.address.street,
      city: view.address.city,
      province_state: view.address.provinceState,
      country: view.address.country,
      postal_code: view.address.postalCode,
    },
    using_agency: view.usingAgency,
    agent_id: view.agentId || null,
    agent_first_name: view.agentFirstName,
    agent_last_name: view.agentLastName,
    agency_company_name: view.agencyCompanyName,
    agent_phone: view.agentPhone,
    agent_email: view.agentEmail,
    agency_notes: view.agencyNotes,
    heard_about_us: view.heardAboutUs,
    heard_about_us_detail: view.heardAboutUsDetail,
    french_goal: view.frenchGoal,
    french_target_level: view.frenchTargetLevel,
    french_current_level: view.frenchCurrentLevel,
    french_test_proof: view.frenchTestProof,
    french_placement_preference: view.frenchPlacementPreference,
    program: view.program,
    selected_add_ons: view.selectedAddOns,
    intended_semester: view.intendedStart,
    campus: view.campus,
    attendance_mode: view.attendanceMode || null,
    pathway_interest: view.universityPathway || null,
  };
}

/* Map an edited view model back to a student's own field names.

   `name` stays the derived display value so everything that reads a student
   by name — exports, PDFs, the students table — keeps working without
   knowing the split fields exist. */
export function personPatchToStudent(person) {
  const view = { ...emptyPerson(), ...(person || {}) };
  return {
    first_name: view.firstName,
    last_name: view.lastName,
    name: joinName(view.firstName, view.lastName),
    preferred_name: view.preferredName,
    email: view.email,
    phone: joinPhone(view.phoneCountryCode, view.phoneNumber),
    dob: view.dateOfBirth,
    gender: view.gender,
    citizenship_country: view.citizenshipCountry,
    status_in_canada: STATUS_CODE_TO_STUDENT[view.statusInCanada] || '',
    status_expiry_date: view.statusExpiryDate,
    unit_number: view.address.unit,
    street_address: view.address.street,
    city: view.address.city,
    province: view.address.provinceState,
    country: view.address.country,
    postal_code: view.address.postalCode,
    agent_id: view.agentId || null,
    heard_about_us: view.heardAboutUs,
    heard_about_us_detail: view.heardAboutUsDetail,
    french_goal: view.frenchGoal,
    french_target_level: view.frenchTargetLevel,
    french_current_level: view.frenchCurrentLevel,
    french_test_proof: view.frenchTestProof,
    french_placement_preference: view.frenchPlacementPreference,
    program_name: view.program,
    selected_add_ons: view.selectedAddOns,
    start_date: view.intendedStart,
    campus: CAMPUS_CODE_TO_STUDENT[view.campus] || '',
    delivery_method: ATTENDANCE_CODE_TO_STUDENT[view.attendanceMode] || '',
    pathway_option: PATHWAY_CODE_TO_STUDENT[view.universityPathway] || '',
  };
}

/* Fold a student's live values onto the application's view model.

   This is what the admissions page renders once an applicant has been
   converted and before the record is locked: the student's current answers,
   but keeping the applicant's own answer wherever the student record is not
   precise enough to contradict it. Fields a student record has no opinion
   about — the agency contact block, the declaration — stay as the applicant
   left them. */
export function mergeStudentOntoApplication(applicationPerson, studentPerson) {
  const base = applicationPerson || emptyPerson();
  if (!studentPerson) return base;
  const live = studentPerson;
  const merged = {
    ...base,
    firstName: live.firstName || base.firstName,
    lastName: live.lastName || base.lastName,
    preferredName: live.preferredName || base.preferredName,
    email: live.email || base.email,
    phoneCountryCode: live.phoneCountryCode || base.phoneCountryCode,
    phoneNumber: live.phoneNumber || base.phoneNumber,
    dateOfBirth: live.dateOfBirth || base.dateOfBirth,
    gender: live.gender || base.gender,
    citizenshipCountry: live.citizenshipCountry || base.citizenshipCountry,
    statusExpiryDate: live.statusExpiryDate || base.statusExpiryDate,
    statusInCanada: reconcileLossy(
      base.statusInCanada,
      STATUS_CODE_TO_STUDENT[live.statusInCanada] || '',
      STATUS_CODE_TO_STUDENT,
      STUDENT_TO_STATUS_CODE,
    ),
    campus: reconcileLossy(
      base.campus,
      CAMPUS_CODE_TO_STUDENT[live.campus] || '',
      CAMPUS_CODE_TO_STUDENT,
      STUDENT_TO_CAMPUS_CODE,
    ),
    attendanceMode: reconcileLossy(
      base.attendanceMode,
      ATTENDANCE_CODE_TO_STUDENT[live.attendanceMode] || '',
      ATTENDANCE_CODE_TO_STUDENT,
      STUDENT_TO_ATTENDANCE_CODE,
    ),
    universityPathway: reconcileLossy(
      base.universityPathway,
      PATHWAY_CODE_TO_STUDENT[live.universityPathway] || '',
      PATHWAY_CODE_TO_STUDENT,
      STUDENT_TO_PATHWAY_CODE,
    ),
    program: live.program || base.program,
    intendedStart: live.intendedStart || base.intendedStart,
    englishProficiencyType: live.englishProficiencyType || base.englishProficiencyType,
    englishProficiencyScore: live.englishProficiencyScore || base.englishProficiencyScore,
    applicationFeePaid: live.applicationFeePaid || base.applicationFeePaid,
    sinLast3: live.sinLast3 || base.sinLast3,
    hasSin: live.hasSin || base.hasSin,
  };
  merged.selectedAddOns = live.selectedAddOns.length ? live.selectedAddOns : base.selectedAddOns;
  // The address moves as one unit. Taking it field by field would blend two
  // addresses into one the person has never lived at.
  const liveAddressFilled = Object.values(live.address).some((value) => String(value || '').trim());
  merged.address = liveAddressFilled ? { ...live.address } : { ...base.address };
  merged.addressLegacy = liveAddressFilled ? '' : live.addressLegacy || base.addressLegacy;
  if (live.agencyDataPresent) {
    ['agentId', 'usingAgency', 'agentFirstName', 'agentLastName', 'agencyCompanyName', 'agentPhone', 'agentEmail', 'agencyNotes'].forEach((key) => { merged[key] = live[key]; });
  } else if (live.agentId) {
    merged.agentId = live.agentId;
    merged.usingAgency = true;
  }
  // Referral answers travel independently of agency contacts. A legacy student
  // with no answer must not hide the answer still held by their application.
  if (live.heardAboutUs) {
    merged.heardAboutUs = live.heardAboutUs;
    merged.heardAboutUsDetail = live.heardAboutUsDetail;
  }
  /* The French answers move as one block, for the same reason the address does:
     they are related to each other, and taking them field by field would show a
     target level from the application beside a goal from the student that no
     longer has one. A student record with nothing to say leaves the
     applicant's answers standing. */
  if (FRENCH_VIEW_FIELDS.some((key) => live[key])) {
    FRENCH_VIEW_FIELDS.forEach((key) => { merged[key] = live[key]; });
  }
  return merged;
}

export default {
  applicationToPerson,
  studentToPerson,
  recordToPerson,
  personPatchToApplication,
  personPatchToStudent,
  mergeStudentOntoApplication,
  emptyPerson,
};
